import { useState } from 'react'
import { ApiError } from '@/api/client'
import { nodes as api } from '@/api/endpoints'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Err } from '@/components/Err'

type Guest = { vmid: number; name: string; type: 'qemu' | 'lxc'; running: boolean; lease: boolean }

// 노드 상세 제목 줄의 고급 칩 `재부팅`(#480). 위에 게스트가 있으면 먼저 보여 주고 확인을 받는다 —
// 켜져 있던 것은 노드가 돌아오면 다시 켜지고, 그것은 고르는 설정이 아니다.
export function NodeRebootChip({ nodeId, className }: { nodeId: string; className: string }) {
  const [open, setOpen] = useState(false)
  const [guests, setGuests] = useState<Guest[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const close = () => {
    setOpen(false)
    setGuests(null)
    setError(null)
    setDone(false)
  }
  const reboot = async (confirm: boolean) => {
    setBusy(true)
    setError(null)
    try {
      await api.reboot(nodeId, confirm)
      setGuests(null)
      setDone(true)
    } catch (err) {
      const listed = err instanceof ApiError && err.reason === 'node_has_guests' ? (err.body as { guests?: Guest[] } | null)?.guests : undefined
      if (listed) setGuests(listed)
      else setError(err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <button type="button" className={className} onClick={() => { setDone(false); setOpen(true) }}>
        재부팅
      </button>
      <Dialog open={open} onOpenChange={(next) => (busy ? undefined : next ? setOpen(true) : close())}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>재부팅</DialogTitle>
          </DialogHeader>
          {done ? (
            <div className="text-muted-foreground">재부팅을 요청했습니다.</div>
          ) : guests ? (
            <div className="grid gap-2">
              <ul className="grid gap-1 text-[13px]">
                {guests.map((g) => (
                  <li key={g.vmid} className="flex items-center justify-between gap-3">
                    <span className="font-medium">{g.name || `VM ${g.vmid}`}</span>
                    <span className="text-muted-foreground">{g.running ? '켜짐' : '꺼짐'}</span>
                  </li>
                ))}
              </ul>
              <div className="text-muted-foreground">켜져 있던 VM 은 노드가 돌아오면 다시 켜집니다.</div>
            </div>
          ) : (
            <div className="text-muted-foreground">노드를 재부팅합니다.</div>
          )}
          <Err error={error} />
          {done ? null : (
            <DialogFooter>
              <Button variant="outline" disabled={busy} onClick={close}>
                취소
              </Button>
              <Button disabled={busy} onClick={() => void reboot(guests !== null)}>
                {busy ? '요청 중' : guests ? 'VM 과 함께 재부팅' : '재부팅'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
